import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// Axiosインスタンスを作成
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 共通: エラーメッセージ抽出ユーティリティ
export const extractApiError = (error) => {
  if (!error) return '不明なエラーが発生しました';
  // FastAPIのdetail優先
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0 && detail[0]?.msg) return detail[0].msg;
  // Axiosのmessage
  if (error.message) return error.message;
  return '処理に失敗しました';
};

// リクエストインターセプター：トークンを自動で付与
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター：トークン期限切れ時の処理
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401エラー（認証エラー）でリトライしていない場合
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // リフレッシュトークンで新しいアクセストークンを取得
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // 別のaxiosインスタンスを使用（無限ループ防止）
          const refreshInstance = axios.create({
            baseURL: '',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const response = await refreshInstance.post('/api/auth/refresh', {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          // 元のリクエストを再実行
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // リフレッシュトークンも無効な場合はログアウト
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // リフレッシュトークンがない場合はセッション期限切れとしてログアウト
        console.warn('Session expired: No refresh token available');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// ================== 認証 API ==================

export const authAPI = {
  /**
   * ログイン
   * OAuth2PasswordRequestForm形式でデータを送信
   */
  login: async (email, password) => {
    // OAuth2の仕様に合わせて、form-data形式で送信
    const formData = new URLSearchParams();
    formData.append('username', email); // usernameフィールドにemailを設定
    formData.append('password', password);

    const response = await api.post('/api/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  /**
   * ログアウト
   */
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  },

  /**
   * トークンリフレッシュ
   */
  refresh: async (refreshToken) => {
    const response = await api.post('/api/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
};

// ================== ユーザー API ==================

export const userAPI = {
  /**
   * ユーザー一覧取得
   */
  getUsers: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },

  /**
   * ユーザー作成
   */
  createUser: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },

  /**
   * ユーザー詳細取得
   */
  getUser: async (userId) => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  },

  /**
   * ユーザー更新
   */
  updateUser: async (userId, userData) => {
    const response = await api.put(`/api/users/${userId}`, userData);
    return response.data;
  },

  /**
   * ユーザー削除
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/users/${userId}`);
    return response.data;
  },

  /**
   * 招待メール再送信
   */
  resendInvitation: async (userId) => {
    const response = await api.post(`/api/users/${userId}/resend-invitation`);
    return response.data;
  },
};

// ================== 自社ユーザー（企業管理者用） API ==================

export const companyUserAPI = {
  /**
   * 自社ユーザー一覧取得（企業管理者以上）
   */
  getUsers: async () => {
    const response = await api.get('/api/company/users');
    return response.data;
  },

  /**
   * 自社ユーザー作成（企業管理者以上、会社は自社固定）
   */
  createUser: async (userData) => {
    const response = await api.post('/api/company/users', userData);
    return response.data;
  },

  /**
   * 自社ユーザー更新（企業管理者以上）
   */
  updateUser: async (userId, userData) => {
    const response = await api.put(`/api/company/users/${userId}`, userData);
    return response.data;
  },

  /**
   * 自社ユーザー削除（企業管理者以上）
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/company/users/${userId}`);
    return response.data;
  },
};

// ================== 企業 API ==================

export const companyAPI = {
  /**
   * 企業一覧取得
   */
  getCompanies: async () => {
    const response = await api.get('/api/companies');
    return response.data;
  },

  /**
   * 企業作成
   */
  createCompany: async (companyData) => {
    const response = await api.post('/api/companies', companyData);
    return response.data;
  },

  /**
   * 企業更新
   */
  updateCompany: async (companyId, companyData) => {
    const response = await api.put(`/api/companies/${companyId}`, companyData);
    return response.data;
  },

  /**
   * 企業削除
   */
  deleteCompany: async (companyId) => {
    const response = await api.delete(`/api/companies/${companyId}`);
    return response.data;
  },
};

// ================== 予約 API ==================

export const reservationAPI = {
  /**
   * 予約一覧取得
   * @param {Object} params - クエリパラメータ { skip, limit, start_date, end_date, room_type, company_id }
   */
  getReservations: async (params = {}) => {
    const response = await api.get('/api/reservations', { params });
    return response.data;
  },

  /**
   * 予約作成
   */
  createReservation: async (reservationData) => {
    const response = await api.post('/api/reservations', reservationData);
    return response.data;
  },

  /**
   * 予約詳細取得
   */
  getReservation: async (reservationId) => {
    const response = await api.get(`/api/reservations/${reservationId}`);
    return response.data;
  },

  /**
   * 予約更新
   */
  updateReservation: async (reservationId, reservationData) => {
    const response = await api.put(`/api/reservations/${reservationId}`, reservationData);
    return response.data;
  },

  /**
   * 予約削除
   * @param {number} reservationId - 予約ID
   * @param {string} scope - 繰り返し予約の削除範囲 ('this', 'future', 'all')
   */
  deleteReservation: async (reservationId, scope = null) => {
    const params = scope ? { scope } : {};
    const response = await api.delete(`/api/reservations/${reservationId}`, { params });
    return response.data;
  },

  /**
   * 競合チェック
   */
  checkConflict: async (conflictData) => {
    const response = await api.post('/api/reservations/check-conflict', conflictData);
    return response.data;
  },

  /**
   * カレンダー用予約データ取得
   */
  getCalendarReservations: async (year, month) => {
    const response = await api.get(`/api/reservations/calendar/${year}/${month}`);
    return response.data;
  },
};

export default api;
